import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { useAuth } from './AuthContext';
import { AppDetailsDrawer, resolveAppDetails } from './AppDetailsDrawer';
import { appTheme } from '../theme';

// Opening the per-app details as a right-side slider over the *current* page
// (rather than routing to a standalone page) keeps the origin page mounted so
// the drawer's blurred backdrop has real content behind it instead of a blank
// screen. Any page can open it via useAppDetailsDrawer().
interface AppDetailsDrawerContextValue {
  openAppDetails: (host: string, platform?: string, appId?: string | number) => void;
  closeAppDetails: () => void;
}

const AppDetailsDrawerContext = createContext<AppDetailsDrawerContextValue>({
  openAppDetails: () => {},
  closeAppDetails: () => {},
});

export const useAppDetailsDrawer = () => useContext(AppDetailsDrawerContext);

type Target = { host: string; platform?: string; appId?: string | number } | null;

export const AppDetailsDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { appData } = useAuth();
  const { pathname } = useLocation();
  const [target, setTarget] = useState<Target>(null);

  const openAppDetails = useCallback((host: string, platform?: string, appId?: string | number) => {
    if (!host) return;
    setTarget({ host, platform, appId });
  }, []);
  const closeAppDetails = useCallback(() => setTarget(null), []);

  // Opening the drawer never changes the route, so a pathname change means the
  // user navigated to another menu — close the drawer instead of leaving it
  // open over the new page.
  useEffect(() => {
    setTarget(null);
  }, [pathname]);

  const app = useMemo(
    () => (target ? resolveAppDetails(appData, target.host, target.platform, target.appId) : null),
    [appData, target],
  );

  const value = useMemo(() => ({ openAppDetails, closeAppDetails }), [openAppDetails, closeAppDetails]);

  return (
    <AppDetailsDrawerContext.Provider value={value}>
      {children}
      {/* The drawer renders at the app root, outside the page-level
          ThemeProvider, so apply the shared theme here too — otherwise it falls
          back to MUI defaults and the close button / styling diverge from the
          in-page drawers. */}
      <ThemeProvider theme={appTheme}>
        <AppDetailsDrawer app={app} onClose={closeAppDetails} />
      </ThemeProvider>
    </AppDetailsDrawerContext.Provider>
  );
};
